/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    $Sprite,
    $Container,
    ContainerProps,
    getProp,
    CanBeSignals,
    $GraphicsBox,
    $NineSlicePlane,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store/Store';
import {RUnit} from '@gongxigames/framework/core/fragments/display/responsive';
import {Container as PixiContainer, DisplayObject, Graphics as PixiGraphics} from 'pixi.js';
import {getLayouts} from './etc';
import {$FullScreenSensor, PointerEvent} from '../FullScreenSensor';
import {$List} from './List';

export type VerticalSelectItem = string;

export type VerticalListSelectProps = Omit<ContainerProps, 'width'|'height'> & CanBeSignals<{
    width: RUnit;
    height: RUnit;
    values: string[];
    currentIndex: number;
    action?: (index: number) => void;
}>;

const MARGIN = 94;
const ITEM_MAX_WIDTH = 444;

export const $VerticalListSelect: FC<VerticalListSelectProps> = (props) => {
    const {action, currentIndex, values, width, height, ...containerProps} = props;
    const mask = Store.signal<PixiGraphics|null>(null);
    const maskSelected = Store.signal<DisplayObject|null>(null);
    const hitBoxContainer = Store.signal<PixiContainer | undefined>();

    const pressed = Store.signal(false);
    const hover = Store.signal(false);

    const posOffsetStart = Store.signal(0);
    const posOffset = Store.signal(0);

    const currentPos = Store.computed(() => {
        const index = getProp(props.currentIndex);

        return -(index * MARGIN) - posOffset.value;
    });

    function setNewIndexByOffset() {
        const length = getProp(props.values).length;
        const height = length * MARGIN;
        let offset = -currentPos.value;

        offset = Math.max(0, offset);
        offset = Math.min(height, offset);

        let index = offset / MARGIN;

        index = Math.round(index);
        index = Math.min(index, length-1);

        setNewIndex(index);
    }

    function setNewIndex(index: number) {
        getProp(props.action)?.(index);

        if (Store.isNotReadonlySignal(props.currentIndex)) {
            (props.currentIndex as Signal).value = index;
        }
    }

    function onScroll(event: Event) {
        const length = getProp(props.values).length;
        let index = getProp(props.currentIndex);

        index += (event as WheelEvent).deltaY > 0 ? 1 : -1;
        index = Math.min(index, length-1);
        index = Math.max(index, 0);

        setNewIndex(index);
    }

    const {responsiveContainer} = getLayouts(props);

    const setPointerDown = (event: PointerEvent) => {
        pressed.value = true;
        posOffsetStart.value = event.data.global.y;
    };
    const setPointerUp = () => {
        pressed.value = false;
        posOffsetStart.value = 0;
        posOffset.value = 0;
    };
    const setHoverTrue = () => {
        hover.value = true;
        document.body.addEventListener('wheel', onScroll);
    };
    const setHoverFalse = () => {
        hover.value = false;
        document.body.removeEventListener('wheel', onScroll);
    };

    function onPointer(event: PointerEvent) {
        if (!event.target || !pressed.value) {
            return;
        }

        const point = event.data.global;

        posOffset.value = posOffsetStart.value - point.y;
    }

    // Do not remove
    <$FullScreenSensor
        onPointerMove={event => {
            onPointer(event);
        }}
        onPointerUp={event => {
            onPointer(event);
            setNewIndexByOffset();
            setPointerUp();
        }}
        enable={pressed}
        hitBoxContainer={hitBoxContainer}
    />;

    return <$Container name='FixedSlider' {...containerProps} responsive={responsiveContainer} mask={mask}>
        <$GraphicsBox
            name='mask'
            ref={item => mask.value = item}
            responsiveBoxSize
            responsive={{
                box: {width: '100%', height: '100%'}
            }}
        />

        <$GraphicsBox
            ref={item => hitBoxContainer.value = item}
            responsiveBoxSize
            responsive={{
                box: {width: '100%', height: '100%'}
            }}
            boxColor={0xff00ff}
            renderable={false}
            interactive={true}
            buttonMode={true}
            events={{
                pointerover: setHoverTrue,
                pointerout: setHoverFalse,
                pointerdown: [setPointerDown, onPointer],
            }}
        />

        <$Container
            name='lists'
            responsive={{
                box: {y: '50%', x: '50%'}
            }}
        >
            <$List
                list={props.values}
                margin={MARGIN}
                maxWidth={ITEM_MAX_WIDTH}
                textStyle='BET_SELECT'
                name='list'
                y={currentPos}
            />

            <$NineSlicePlane
                name='selected_bg'
                texture='bet_selection_area_sys_ui'
                slices={[0, 58]}
                responsive={{
                    box: {width: '512px', height: '152px'},
                    translate: {x: '-50%', y: '-50%'},
                    updateWidth: true,
                }}
            />

            <$GraphicsBox
                name='selected_bg_mask'
                ref={item => maskSelected.value = item}
                responsiveBoxSize
                responsive={{
                    box: {width: '100%', height: '84px'},
                    translate: {y : '-50%', x : '-50%'},
                }}
            />

            <$List
                list={props.values}
                margin={MARGIN}
                maxWidth={ITEM_MAX_WIDTH}
                textStyle='BET_SELECT_HIGHLIGH'
                name='list_masked'
                y={currentPos}
                mask={maskSelected as any}
            />

        </$Container>
        <$Sprite
            texture='bet_select_gradient_sys_ui'
            name='gradient1'
            responsive={{
                box: {width: '100%'},
                updateWidth: true,
            }}
        />
        <$Sprite
            texture='bet_select_gradient_sys_ui'
            name='gradient2'
            rotation={Math.PI}
            anchor={[1, 0]}
            responsive={{
                box: {width: '100%', y: '100%'},
                updateWidth: true,
            }}
        />
    </$Container>;
};